import { useState } from 'react'
import { Box, Typography, IconButton, Divider, Chip } from '@mui/material'
import {
  LocalShipping as TruckIcon,
  Person as DriverIcon,
  Phone as PhoneIcon,
  Group as TeamIcon,
  PlayCircleOutline as StartPointIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material'
import { LoadDetails, LoadDispatch, LoadStop } from '@/types/load'
import {
  formatPhoneNumber,
  formatCarrierIdentifier,
  getDriverName,
  getDriverPhone
} from '@/utils/formatters'
import { formatStopAddress, getFacilityName } from '@/utils/addressFormat'
import { getAllStopsSorted, formatStopSchedule } from '@/utils/stopUtils'
import {
  getMainDispatches,
  findEmptyMoveSibling,
  isContinuationDispatch,
  getDispatchMileage,
  getDispatchStartStop,
  formatMiles
} from '@/utils/dispatchUtils'

interface DispatchDetailsProps {
  load: LoadDetails
}

interface DispatchMileage {
  emMiles: number
  routeMiles: number
  totalMiles: number
}

// P2-1290: same copy as the web timeline (DispatchTimelineRows.tsx). Shown
// alongside the always-rendered ~0-mi empty-move leg when the same driver
// continued straight from the previous dispatch (no real empty move needed).
const CONTINUATION_NOTE =
  'Same Driver continued straight away from last dispatch'

/**
 * One main dispatch card. Empty-move dispatches are NOT rendered as their
 * own card — they're bundled into their linked main (start point + miles),
 * mirroring the web's expanded dispatch view (P2-1306). `number` is the
 * main-dispatch ordinal (1-based), not the raw array index, so the EM
 * sibling no longer occupies a "Dispatch N" slot (P2-1304).
 */
const SingleDispatchCard = ({
  dispatch,
  number,
  total,
  emNumber,
  isContinuation,
  mileage,
  startStop
}: {
  dispatch: LoadDispatch
  number: number
  total: number
  emNumber?: number
  isContinuation: boolean
  mileage: DispatchMileage
  startStop: LoadStop | null
}) => {
  const carrierName =
    dispatch?.carrier?.carrierDetails?.carrierName ||
    dispatch?.carrier?.branch?.name ||
    ''
  const carrierIdentifier = formatCarrierIdentifier(
    dispatch?.carrier?.carrierDetails
  )

  const driverName = dispatch?.driverDetails
    ? getDriverName(dispatch.driverDetails)
    : dispatch?.driver?.name || ''
  const driverPhone = dispatch?.driverDetails
    ? getDriverPhone(dispatch.driverDetails)
    : dispatch?.driver?.phone || ''

  const extraDrivers = dispatch?.extraDrivers || []

  const truckNumber =
    dispatch?.truckDetails?.truckNumber || dispatch?.truck?.number
  const trailerNumber =
    dispatch?.trailerDetails?.trailerNumber || dispatch?.trailer?.number

  const dispatchId = dispatch?.dispatchId

  // P2-1304 Start Point — the dispatch's OWN first stop (mirrors the web's
  // DashboardDispatch "Start Facility: stops[0]"). Sourced from the dispatch's
  // resolved stops, NOT from driverStartLocation (which the backend populates
  // for at most one dispatch — the legacy behavior that caused the start point
  // to be blank on every other dispatch and mis-attributed on the one that had
  // it). The empty-move origin is conveyed by the Empty Move miles below.
  const startFacility = startStop ? getFacilityName(startStop) : ''
  const startAddress = startStop ? formatStopAddress(startStop) : ''
  // Always shown, like the web's stop rows: a missing side reads 'Not Scheduled'
  // and a stop with no active appointment option says so
  const startSchedule = startStop ? formatStopSchedule(startStop) : null
  const showStartPoint = isContinuation || !!startFacility || !!startAddress

  // P2-1290: continuations render an always-present ~0-mi empty-move leg, so
  // the miles block must show even when emMiles/routeMiles are both 0.
  const hasMiles =
    mileage.emMiles > 0 || mileage.routeMiles > 0 || isContinuation

  const hasDispatchInfo =
    carrierName ||
    driverName ||
    truckNumber ||
    trailerNumber ||
    dispatchId ||
    showStartPoint ||
    hasMiles

  if (!hasDispatchInfo) {
    return null
  }

  const handlePhoneClick = (phone: string) => {
    // Use window.top to escape iframe context for tel: links
    try {
      if (window.top) {
        window.top.location.href = `tel:${phone}`
      } else {
        window.open(`tel:${phone}`, '_blank')
      }
    } catch {
      window.open(`tel:${phone}`, '_blank')
    }
  }

  return (
    <Box
      sx={{
        ...(total > 1 && {
          p: 1.5,
          bgcolor: 'grey.50',
          borderRadius: 1,
          mb: number < total ? 1.5 : 0
        })
      }}
    >
      {/* Dispatch Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {total > 1 ? `Dispatch ${number}` : ''}
        </Typography>
        {dispatchId && (
          <Chip
            label={`#${dispatchId}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500 }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Carrier */}
        {carrierName && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <TruckIcon fontSize="small" sx={{ color: 'grey.500', mt: 0.25 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {carrierName}
              </Typography>
              {carrierIdentifier && (
                <Typography variant="caption" color="text.secondary">
                  {carrierIdentifier}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Driver */}
        {driverName && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <DriverIcon
                fontSize="small"
                sx={{ color: 'grey.500', mt: 0.25 }}
              />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {driverName}
                </Typography>
                {driverPhone && (
                  <Typography variant="caption" color="text.secondary">
                    {formatPhoneNumber(driverPhone)}
                  </Typography>
                )}
              </Box>
            </Box>
            {driverPhone && (
              <IconButton
                size="small"
                onClick={() => handlePhoneClick(driverPhone)}
                sx={{
                  color: 'primary.main',
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'primary.lighter' }
                }}
              >
                <PhoneIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}

        {/* Extra Drivers (Team) */}
        {extraDrivers.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <TeamIcon fontSize="small" sx={{ color: 'grey.500', mt: 0.25 }} />
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Team Drivers
              </Typography>
              {extraDrivers.map((driver, idx) => {
                const name = getDriverName(driver)
                const phone = getDriverPhone(driver)
                return (
                  <Box
                    key={driver._id || idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 0.5
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {name}
                      </Typography>
                      {phone && (
                        <Typography variant="caption" color="text.secondary">
                          {formatPhoneNumber(phone)}
                        </Typography>
                      )}
                    </Box>
                    {phone && (
                      <IconButton
                        size="small"
                        onClick={() => handlePhoneClick(phone)}
                        sx={{ p: 0.5 }}
                      >
                        <PhoneIcon
                          sx={{ fontSize: 14, color: 'primary.main' }}
                        />
                      </IconButton>
                    )}
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}

        {/* Equipment */}
        {(truckNumber || trailerNumber) && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', gap: 4 }}>
              {truckNumber && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    Truck
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {truckNumber}
                  </Typography>
                </Box>
              )}
              {trailerNumber && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    Trailer
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {trailerNumber}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* P2-1306 Miles — empty-move leg + dispatch route */}
        {hasMiles && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', gap: 4 }}>
              {(mileage.emMiles > 0 || isContinuation) && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    {emNumber ? `Empty Move #${emNumber}` : 'Empty Move'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatMiles(mileage.emMiles)}
                  </Typography>
                </Box>
              )}
              {mileage.routeMiles > 0 && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    Route
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatMiles(mileage.routeMiles)}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* P2-1166 Start Point */}
        {showStartPoint && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <StartPointIcon
                fontSize="small"
                sx={{ color: 'grey.500', mt: 0.25 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', lineHeight: 1.2 }}
                >
                  Start Point
                </Typography>
                {isContinuation ? (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontStyle: 'italic',
                      color: 'text.secondary'
                    }}
                  >
                    {CONTINUATION_NOTE}
                  </Typography>
                ) : (
                  <>
                    {startFacility && (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, wordBreak: 'break-word' }}
                      >
                        {startFacility}
                      </Typography>
                    )}
                    {startAddress && (
                      <Typography
                        variant={startFacility ? 'caption' : 'body2'}
                        color={startFacility ? 'text.secondary' : undefined}
                        sx={{
                          display: 'block',
                          fontWeight: startFacility ? 400 : 500,
                          wordBreak: 'break-word',
                          lineHeight: 1.4
                        }}
                      >
                        {startAddress}
                      </Typography>
                    )}
                    {startSchedule && (
                      <Typography variant="caption" color="text.secondary">
                        {startSchedule}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

const DispatchDetails = ({ load }: DispatchDetailsProps) => {
  const [expanded, setExpanded] = useState(false)

  const allDispatches = load.dispatches || []

  if (allDispatches.length === 0) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Dispatch
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No dispatch assigned
        </Typography>
      </Box>
    )
  }

  // Number/iterate MAIN dispatches only; empty-move dispatches are bundled
  // into their linked main below (P2-1304/P2-1306). Fall back to the raw
  // list if the data somehow has no main dispatch.
  const sortedStops = getAllStopsSorted(load.stops, load.emptyMoveStops)
  const mains = getMainDispatches(allDispatches)
  const baseList = mains.length ? mains : allDispatches

  // P2-1271: empty moves get their own number series ("Empty Move #N"),
  // independent of the "Dispatch N" numbering. Only rows that actually show an
  // empty-move leg (emMiles > 0) receive one.
  let emSeq = 0
  const rows = baseList.map((main, idx) => {
    const emSibling = mains.length
      ? findEmptyMoveSibling(main, allDispatches)
      : null
    const prevMain = idx > 0 ? baseList[idx - 1] : undefined
    const isContinuation = isContinuationDispatch(main, prevMain)
    const mileage = getDispatchMileage(
      main,
      emSibling,
      sortedStops,
      isContinuation
    )
    // P2-1304 — a continuation dispatch shows the "—"/note (Case A), so it
    // needs no start stop; every other dispatch shows its own first stop.
    const startStop = isContinuation
      ? null
      : getDispatchStartStop(main, sortedStops)
    const emNumber = mileage.emMiles > 0 ? ++emSeq : undefined
    return {
      main,
      number: idx + 1,
      emNumber,
      isContinuation,
      mileage,
      startStop
    }
  })

  const total = rows.length
  const multi = total > 1
  const totalMiles = rows.reduce((sum, r) => sum + r.mileage.totalMiles, 0)

  return (
    <Box sx={{ p: 2.5 }}>
      {/* Header — title + collapse/expand toggle (multi-dispatch only) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: multi && expanded ? 2 : multi ? 0 : 2
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {multi ? `Dispatches (${total})` : 'Dispatch'}
        </Typography>
        {multi && (
          <IconButton
            size="small"
            onClick={() => setExpanded(prev => !prev)}
            aria-label={expanded ? 'Collapse dispatches' : 'Expand dispatches'}
            sx={{ color: 'text.secondary' }}
          >
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>

      {multi && !expanded ? (
        // Collapsed: single summary line, click to expand (mirrors web's
        // collapsed dispatch state).
        <Box
          onClick={() => setExpanded(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
            cursor: 'pointer'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {total} dispatches
            {totalMiles > 0 ? ` · ${formatMiles(totalMiles)} total` : ''}
          </Typography>
          <Typography variant="caption" color="primary">
            Show details
          </Typography>
        </Box>
      ) : (
        rows.map(r => (
          <SingleDispatchCard
            key={r.main._id || r.main.dispatchId || r.number}
            dispatch={r.main}
            number={r.number}
            total={total}
            emNumber={r.emNumber}
            isContinuation={r.isContinuation}
            mileage={r.mileage}
            startStop={r.startStop}
          />
        ))
      )}
    </Box>
  )
}

export default DispatchDetails
